import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/api/client';
import type { ShareLink } from '@shared/types';

interface ShareModalProps {
  scrapbookId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ShareModal({ scrapbookId, isOpen, onClose }: ShareModalProps) {
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const linkInputRef = useRef<HTMLInputElement>(null);
  const hasGeneratedRef = useRef(false);

  const generateShareLink = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<{ shareLink: ShareLink }>(
        `/scrapbooks/${scrapbookId}/share`
      );
      setShareLink(response.shareLink);
    } catch (err) {
      setError('Failed to generate share link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [scrapbookId]);

  // Generate share link when modal opens
  useEffect(() => {
    if (isOpen && !hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      generateShareLink();
    }
  }, [isOpen, generateShareLink]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShareLink(null);
      setError(null);
      setCopied(false);
      hasGeneratedRef.current = false;
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, onClose]);

  const handleCopy = async () => {
    if (!shareLink) return;

    try {
      await navigator.clipboard.writeText(shareLink.url);
      setCopied(true);
      linkInputRef.current?.select();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      if (linkInputRef.current) {
        linkInputRef.current.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="modal modal-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        <div className="modal-header">
          <h2 id="share-modal-title" className="modal-title">
            Share Scrapbook
          </h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {isLoading && (
            <div className="share-modal-loading">
              <div className="spinner" aria-hidden="true" />
              <p>Generating share link...</p>
            </div>
          )}

          {error && (
            <div className="share-modal-error" role="alert">
              <p>{error}</p>
              <button
                className="btn btn-secondary btn-sm"
                onClick={generateShareLink}
              >
                Try Again
              </button>
            </div>
          )}

          {shareLink && !isLoading && !error && (
            <>
              <p className="share-modal-description">
                Anyone with this link can view your scrapbook:
              </p>

              <div className="share-modal-link-group">
                <input
                  ref={linkInputRef}
                  type="text"
                  className="form-input share-modal-link-input"
                  value={shareLink.url}
                  readOnly
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  aria-label="Share link"
                />
                <button
                  className={`btn ${copied ? 'btn-success' : 'btn-primary'}`}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M13.5 4.5L6 12L2.5 8.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <rect
                          x="5"
                          y="5"
                          width="9"
                          height="9"
                          rx="1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M11 5V3C11 2.44772 10.5523 2 10 2H3C2.44772 2 2 2.44772 2 3V10C2 10.5523 2.44772 11 3 11H5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                      Copy Link
                    </>
                  )}
                </button>
              </div>

              {shareLink.viewCount > 0 && (
                <p className="share-modal-stats">
                  Viewed {shareLink.viewCount} time{shareLink.viewCount === 1 ? '' : 's'}
                </p>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
